/**
 *
 * @module paywallApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/paywall.md
 *
 */

import Check from 'check-types';

import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import PaywallManager from './paywallManager';

const TokenManager = Token.TokenManager;

const Util = Services.Util;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const PaywallApiDustUrnReference = Services.Internal.Dust.DustUrnReference.paywall.paywallApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class PaywallApi
 * @since 3.9.0
 * @desc Provides available product information and account context.
 *
 */
export default class PaywallApi {

    /**
     *
     * @param {Object} options
     * @param {SDK.Paywall.PaywallManager} options.paywallManager
     * @param {SDK.Token.TokenManager} options.tokenManager
     * @param {SDK.Logging.Logger} [options.logger=Logging]
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: `${this.constructor.name}(options)`,
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get paywallManager() {
                return `${this.ctor} needs SDK.Paywall.PaywallManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get logger() {
                return `${this.ctor} needs SDK.Logger instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { paywallManager, tokenManager, logger = Logging } = options;

        instanceStrict(paywallManager, PaywallManager, errorMessage.paywallManager, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Paywall.PaywallManager}
         *
         */
        this.paywallManager = paywallManager;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.paywallManager.client.dustEnabled);

        this.logger.info(PaywallApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @desc Returns a paywall object containing sku, campaign and account context information for
     * a given country, platform and account/device.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Paywall.Paywall>}
     *
     */
    async getPaywall() {

        const { paywallManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PaywallApi.name,
            urn: PaywallApiDustUrnReference.getPaywall
        });

        try {

            return await paywallManager.getPaywall();

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

}



// WEBPACK FOOTER //
// ./src/paywall/paywallApi.js